import React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react/dist/ckeditor';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { MyCustomUploadAdapterPlugin } from '../utils/ckEditorAdapter';
import { optionalCharError, creativeProcessError, getPureTextLength, getImageCount } from 'src/common/utils/charValidation';

interface BookOptionalSectionProps {
    desktop: boolean;
    planBackground: string;
    setPlanBackground: (v: string) => void;
    creativeProcessText: string;
    setCreativeProcessText: (v: string) => void;
    differenceFromPrev: string;
    setDifferenceFromPrev: (v: string) => void;
    readerMessage: string;
    setReaderMessage: (v: string) => void;
    toneDecisionProcess: string;
    setToneDecisionProcess: (v: string) => void;
}

const textareaCls =
    'appearance-none w-full h-36 font-namsan border border-extra placeholder-[#5E676E] focus:outline-input rounded py-3 px-4 leading-tight focus:text-black';

const CountRow = ({ val }: { val: string }) => (
    <div className="flex items-center justify-between mt-1">
        {optionalCharError(val)
            ? <p className="font-namsan font-bold text-red-500">{optionalCharError(val)}</p>
            : <span />
        }
        <span className={`text-xs ${val.length > 1000 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
            {val.length}/1000
        </span>
    </div>
);

const BookOptionalSection: React.FC<BookOptionalSectionProps> = ({
    desktop,
    planBackground, setPlanBackground,
    creativeProcessText, setCreativeProcessText,
    differenceFromPrev, setDifferenceFromPrev,
    readerMessage, setReaderMessage,
    toneDecisionProcess, setToneDecisionProcess,
}) => {
    const labelCls = desktop ? 'subTitle' : 'text-base font-bold text-black';
    const wrapCls = desktop ? 'flex flex-col gap-6 pt-6 border-t border-gray-200' : 'flex flex-col gap-5 pt-4 border-t border-gray-200';
    const headingCls = desktop ? 'title' : 'text-base font-bold text-main';

    return (
        <div className={wrapCls}>
            <p className={headingCls} style={{ color: '#19CF7A' }}>
                추가 정보 <span className="text-[#000000] text-sm font-normal ml-2" style={{ color: '#000000' }}>아래 항목은 필수가 아닌 선택 사항입니다! 😊</span>
            </p>

            {/* 작품 기획 배경 */}
            <div className="flex flex-col gap-2">
                <p className={labelCls}>작품 기획 배경</p>
                <textarea
                    className={textareaCls}
                    value={planBackground}
                    onChange={(e) => setPlanBackground(e.target.value)}
                    placeholder="이 작품을 기획하게 된 배경을 알려주세요."
                    maxLength={1000}
                />
                <CountRow val={planBackground} />
            </div>

            {/* 창작 과정 – CKEditor */}
            <div className="flex flex-col gap-2">
                <p className={labelCls}>작품 창작 과정을 독자에게 알려 보세요!</p>
                <style>{`.ck-editor__editable_inline { min-height: 350px !important; }`}</style>
                <div className="border border-extra rounded overflow-hidden">
                    <CKEditor
                        editor={ClassicEditor as any}
                        data={creativeProcessText}
                        onChange={(_: any, editor: any) => setCreativeProcessText(editor.getData())}
                        config={{
                            placeholder: '작품이 만들어진 과정이나 비하인드를 자유롭게 작성해주세요. 이미지도 함께 첨부할 수 있어요 😊',
                            extraPlugins: [MyCustomUploadAdapterPlugin],
                            toolbar: ['bold', 'italic', '|', 'bulletedList', 'numberedList', '|', 'imageUpload', '|', 'undo', 'redo'],
                        }}
                    />
                </div>
                <div className="flex items-center justify-between mt-1">
                    {creativeProcessError(creativeProcessText) ? (
                        <p className="font-namsan font-bold text-red-500">{creativeProcessError(creativeProcessText)}</p>
                    ) : (
                        <span />
                    )}
                    <span className={`text-xs ${getPureTextLength(creativeProcessText) > 1000 || getImageCount(creativeProcessText) > 3 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                        텍스트 {getPureTextLength(creativeProcessText)}/1000 | 이미지 {getImageCount(creativeProcessText)}/3
                    </span>
                </div>
            </div>

            {/* 전작과 달라진 점 */}
            <div className="flex flex-col gap-2">
                <p className={labelCls}>이전에 출간한 작품이 있다면, 이번 글에서 달라진 점은 무엇인가요?</p>
                <textarea
                    className={textareaCls}
                    value={differenceFromPrev}
                    onChange={(e) => setDifferenceFromPrev(e.target.value)}
                    placeholder="이전 출간작이 있는 경우에만 작성해 주세요."
                    maxLength={1000}
                />
                <CountRow val={differenceFromPrev} />
            </div>

            {/* 독자에게 전하고 싶은 메시지 */}
            <div className="flex flex-col gap-2">
                <p className={labelCls}>이번 작품을 통해 독자에게 전하고 싶은 메시지를 작성해 보세요!</p>
                <textarea
                    className={textareaCls}
                    value={readerMessage}
                    onChange={(e) => setReaderMessage(e.target.value)}
                    placeholder="독자에게 전하고 싶은 메시지를 자유롭게 적어주세요."
                    maxLength={1000}
                />
                <CountRow val={readerMessage} />
            </div>
            
             {/* 작품 톤 결정 과정 */}
            <div className="flex flex-col gap-2">
                <p className={labelCls}>작품의 전체적인 톤은 어떻게 결정하셨나요?</p>
                <textarea
                    className={textareaCls}
                    value={toneDecisionProcess}
                    onChange={(e) => setToneDecisionProcess(e.target.value)}
                    placeholder="작품의 분위기나 어조를 결정하게 된 과정을 자유롭게 적어주세요."
                    maxLength={1000}
                />
                <CountRow val={toneDecisionProcess} />
            </div>
        </div>
    );
};

export default BookOptionalSection;
